import React,{ useState, useEffect } from 'react';
import { downloadCsv } from '../common/sharedFunctions';
import CircularLoading from "../components/CircularLoading";
import { useSelector, useDispatch } from "react-redux";
import { api } from 'common';
import { useTranslation } from "react-i18next";
import moment from 'moment';
import { Box, Grid, Typography } from '@mui/material';
import { formatCurrency, labelText } from "../common/sharedFunctions";
import localization from '../components/Table/Localization';
import AdminDataTable from "../components/AdminDataTable";
import AdminStatusBadge from "../components/AdminStatus";
import { PageHeader, SectionHeader, StatusBadge, SurfaceCard } from "../components/adminPrimitives";
import adminTokens from "../styles/adminDesignTokens";

const Withdraws = () => {
  const { t,i18n } = useTranslation();
  const isRTL = i18n.dir();
  const label = (key, fallback) => labelText(t, key, fallback);
  const settings = useSelector(state => state.settingsdata.settings);
  const {
    completeWithdraw
  } = api;
  const dispatch = useDispatch();

  const columns =  [
    { title: 'ID', field: 'id',editable: 'never',
  },
    { title: t('requestDate'), field: 'date', defaultSort:'desc', render: rowData => rowData.date ? moment(rowData.date).format('lll') : null,
              exportTransformer: (rowData) => new Date(rowData.date).toLocaleDateString() + ' '+ new Date(rowData.date).toLocaleTimeString()},
    { title: t('driver_name'),field: 'name',editable: 'never', 
  },
  { title: t('amount'), field: 'amount',editable: 'never',
    render: (rowData) =>
    formatCurrency(rowData.amount || 0, settings),
  },
    { title: t('processed'), field: 'processed', type: 'boolean',editable: 'never',
      render: rowData => <AdminStatusBadge status={rowData.processed ? "COMPLETE" : "PENDING"}>{rowData.processed ? label("processed", "Processed") : label("pending", "Pending")}</AdminStatusBadge>
  },  
    { title: t('processDate'), field: 'processDate', render: rowData => rowData.processDate ? moment(rowData.processDate).format('lll') : null,
  },
    { title: t('bankName'), field: 'bankName', hidden: settings.bank_fields===false? true: false,editable: 'never'},
    { title: t('bankCode'), field: 'bankCode' , hidden: settings.bank_fields===false? true: false,editable: 'never'},
    { title: t('bankAccount'), field: 'bankAccount', hidden: settings.bank_fields===false? true: false,editable: 'never'}
  ];
  const [data, setData] = useState([]);
  const withdrawdata = useSelector(state => state.withdrawdata);

  useEffect(()=>{
        if(withdrawdata.withdraws){
            setData(withdrawdata.withdraws);
        }else{
          setData([]);
        }
  },[withdrawdata.withdraws]);

  const [selectedRow, setSelectedRow] = useState(null);
  const processedCount = data.filter((item) => item.processed).length;
  const pendingCount = data.length - processedCount;
  const visibleTotal = data.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  return (
    withdrawdata.loading? <CircularLoading/>:
    <Box className="garahex-finance-withdraws-page" sx={{ direction: isRTL === "rtl" ? "rtl" : "ltr" }}>
      <PageHeader
        eyebrow={label("finance", "Finance")}
        title={t('Withdraw_title')}
        description={label("withdraws_admin_description", "Review withdrawal requests and use the existing process action when a request is ready.")}
        actions={<StatusBadge tone="warning">{`${pendingCount} ${label("pending", "pending")}`}</StatusBadge>}
      />
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <SurfaceCard sx={{ minHeight: 112 }}>
            <Typography sx={{ color: adminTokens.colors.muted, fontSize: 13, fontWeight: 850 }}>{label("withdraw_requests", "Withdraw Requests")}</Typography>
            <Typography sx={{ color: adminTokens.colors.text, fontSize: "1.9rem", fontWeight: 950 }}>{data.length}</Typography>
          </SurfaceCard>
        </Grid>
        <Grid item xs={12} sm={4}>
          <SurfaceCard sx={{ minHeight: 112 }}>
            <Typography sx={{ color: adminTokens.colors.muted, fontSize: 13, fontWeight: 850 }}>{label("processed", "Processed")}</Typography>
            <Typography sx={{ color: adminTokens.colors.success, fontSize: "1.9rem", fontWeight: 950 }}>{processedCount}</Typography>
          </SurfaceCard>
        </Grid>
        <Grid item xs={12} sm={4}>
          <SurfaceCard sx={{ minHeight: 112 }}>
            <Typography sx={{ color: adminTokens.colors.muted, fontSize: 13, fontWeight: 850 }}>{label("visible_amount", "Visible Amount")}</Typography>
            <Typography sx={{ color: adminTokens.colors.garahexOrange, fontSize: "1.45rem", fontWeight: 950 }}>{formatCurrency(visibleTotal || 0, settings)}</Typography>
          </SurfaceCard>
        </Grid>
      </Grid>
      <SurfaceCard>
        <SectionHeader
          title={t('Withdraw_title')}
          description={label("withdraws_table_description", "CSV export and the existing process-withdraw action remain unchanged.")}
        />
        <AdminDataTable
          title={t('Withdraw_title')}
          columns={columns}
          style={{ direction: isRTL === "rtl" ? "rtl" : "ltr" }}
          data={data}
          emptyTitle={label("no_withdraw_requests_found", "No withdraw requests found")}
          emptyDescription={label("no_withdraw_requests_found_description", "No withdrawal requests are available in the current finance scope.")}
          onRowClick={((evt, selectedRow) => setSelectedRow(selectedRow.tableData.id))}
          options={{
            exportCsv: (columns, data) => {
              let hArray = [];
              const headerRow = columns.map(col => {
                if (typeof col.title === 'object') {
                  return col.title.props.text;
                }
                hArray.push(col.field);
                return col.title;
              });
              const dataRows = data.map(({ tableData, ...row }) => {
                row.date = new Date(row.date).toLocaleDateString() + ' '+ new Date(row.date).toLocaleTimeString()
                row.processDate = row.processDate? new Date(row.processDate).toLocaleDateString() + ' '+ new Date(row.processDate).toLocaleTimeString(): ''
                let dArr = [];
                for(let i=0;i< hArray.length; i++) {
                  dArr.push(row[hArray[i]]);
                }
                return Object.values(dArr);
              })
              const { exportDelimiter } = ",";
              const delimiter = exportDelimiter ? exportDelimiter : ",";
              const csvContent = [headerRow, ...dataRows].map(e => e.join(delimiter)).join("\n");
              const csvFileName = 'download.csv';
              downloadCsv(csvContent, csvFileName);
            },
            exportButton: {
              csv: settings.AllowCriticalEditsAdmin,
              pdf: false,
            },
            rowStyle: (rowData) => ({
              backgroundColor:
                selectedRow === rowData.tableData.id ? "rgba(11, 124, 255, 0.09)" : "#FFFFFF"
            }),
          }}
          localization={localization(t)}
          actions={[
            rowData => ({
              icon: 'check',
              tooltip: t('process_withdraw'),
              disabled: rowData.processed,
              onClick: (event, rowData) => {
                dispatch(completeWithdraw(rowData));
              }
            }),
          ]}
        />
      </SurfaceCard>
    </Box>
  );
}

export default Withdraws;
